// frontend/src/features/livestock/useInboxSorting.ts
/**
 * Assign and discard without waiting (plan 4.4): the assign view moves on to
 * the next photo as soon as the user acts, and the request finishes in the
 * background, recorded in sortLog. A photo is never sent twice at once. A
 * failure stays in the log with its reason until the user retries it, opens
 * the photo again or dismisses it; undo works for 15 minutes.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutationState } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { PhotoInboxBusyError } from '@/api/photoInbox';
import {
  INBOX_ROUTE,
  InboxSessionChangedError,
  useAssignInboxPhoto,
  useDiscardInboxPhoto,
  useRestoreInboxPhoto,
} from '@/hooks/usePhotoInbox';
import { photoInboxKeys } from '@/hooks/photoInboxKeys';
import type { Livestock } from '@/types/livestock';
import {
  isTankResult,
  type PhotoInboxAssignResult,
  type PhotoInboxAssignToOrganism,
  type PhotoInboxAssignToTank,
  type PhotoInboxItem,
} from '@/types/photoInbox';
import type { InboxLocationState } from './InboxPhotoGrid';
import {
  assignedElsewhere,
  describeSortFailure,
  describeUndoFailure,
  isConflict,
} from './photoInboxErrors';
import { pushRecent, readRecents, writeRecents } from './photoInboxRecents';
import {
  canUndo,
  sortLog,
  TANK_SHOT_NAME,
  targetOf,
  useSortRecords,
  type SortRecord,
} from './photoInboxSorting';
import { useOpenInTank } from './useOpenInTank';

const PHOTO_ROUTE = /^\/livestock\/photos\/\d+/;

/** An assign to an organism: the organism picked and the request naming it. */
export interface OrganismAssignInput {
  photo: PhotoInboxItem;
  organism: Livestock;
  body: PhotoInboxAssignToOrganism;
}

/** An assign as a tank shot: no organism. */
export interface TankAssignInput {
  photo: PhotoInboxItem;
  organism?: null;
  body: PhotoInboxAssignToTank;
}

/** What the assign view sends; the request's target and the organism can't disagree. */
export type AssignInput = OrganismAssignInput | TankAssignInput;

function isTankInput(input: AssignInput): input is TankAssignInput {
  return input.body.target === 'tank';
}

export interface InboxSorting {
  /** Sends the assign; false when this photo is being sent already. */
  assign: (input: AssignInput) => boolean;
  /** Sends the discard; false when this photo is being sent already. */
  discard: (photo: PhotoInboxItem) => boolean;
  /** Sends a failed assign or discard again. */
  retry: (key: number) => void;
  /** Takes an assign or discard back and opens the photo again. */
  undo: (key: number) => void;
  /** Opens the photo of a failed assign or discard, to choose again. */
  open: (key: number) => void;
  /** Hides a failure. */
  dismiss: (key: number) => void;
}

/**
 * Sorting for one tank. `organisms` names the organism a photo turns out to
 * be on already (the server answers a repeated assign with the first one).
 */
export function useInboxSorting(tankId: string, organisms: Livestock[] = []): InboxSorting {
  // Each a stable function, which keeps working after this component unmounts.
  const { mutateAsync: sendAssign } = useAssignInboxPhoto();
  const { mutateAsync: sendDiscard } = useDiscardInboxPhoto();
  const { mutateAsync: sendRestore } = useRestoreInboxPhoto();
  const location = useLocation();
  const openInTank = useOpenInTank();

  // Read when an answer arrives, maybe after this screen has gone.
  const latest = useRef({ organisms, location });
  useEffect(() => {
    latest.current = { organisms, location };
  });

  const send = useCallback(
    (record: SortRecord): void => {
      /** The organism wasn't a recent choice after all: the Recent chips as they were. */
      const putRecentsBack = (): void => {
        if (record.recentsBefore && readRecents(tankId)[0] === record.livestockId) {
          writeRecents(tankId, record.recentsBefore);
        }
      };
      const assigned = (answer: PhotoInboxAssignResult): void => {
        if (isTankResult(answer)) {
          sortLog.update(record.key, {
            status: 'done',
            doneAt: Date.now(),
            resultTarget: 'tank',
            resultLivestockName: TANK_SHOT_NAME,
          });
          return;
        }
        const resultId = answer.livestock_id;
        const asChosen = resultId === record.livestockId;
        // It went to the organism it was on already.
        if (!asChosen) putRecentsBack();
        sortLog.update(record.key, {
          status: 'done',
          doneAt: Date.now(),
          resultTarget: 'organism',
          resultLivestockId: resultId,
          resultLivestockName: asChosen
            ? record.livestockName
            : (latest.current.organisms.find((organism) => organism.id === resultId)?.common_name ??
              'another organism'),
        });
      };
      const request: Promise<unknown> =
        record.kind === 'assign' && record.body
          ? sendAssign({ tankId, id: record.photoId, body: record.body }).then(assigned)
          : sendDiscard({ tankId, id: record.photoId }).then(() => {
              sortLog.update(record.key, { status: 'done', doneAt: Date.now() });
            });
      request.catch((error: unknown) => {
        if (error instanceof InboxSessionChangedError) return; // signed out: the log is gone
        const elsewhere = assignedElsewhere(error);
        if (elsewhere) {
          // A tank shot already when an organism was picked, or the reverse:
          // nothing was assigned here, so there is nothing to undo either.
          putRecentsBack();
          sortLog.update(record.key, {
            status: 'done',
            doneAt: null,
            elsewhere: true,
            resultTarget: elsewhere.target,
            resultLivestockName:
              elsewhere.target === 'tank'
                ? TANK_SHOT_NAME
                : (elsewhere.livestock_name ?? 'another organism'),
          });
          return;
        }
        const why = describeSortFailure(error);
        if (!why) {
          sortLog.update(record.key, {
            status: 'gone',
            message:
              record.kind === 'discard' && isConflict(error, 'not_pending')
                ? 'assigned meanwhile'
                : 'sorted already',
          });
          return;
        }
        sortLog.update(record.key, {
          status: 'failed',
          message: why.message,
          canRetry: why.canRetry,
          dismissed: false,
        });
      });
    },
    [tankId, sendAssign, sendDiscard]
  );

  const start = useCallback(
    (photo: PhotoInboxItem, fields: Partial<SortRecord> & Pick<SortRecord, 'kind'>): boolean => {
      if (sortLog.pendingFor(tankId, photo.id)) return false;
      // A new choice for a photo whose send failed replaces that failure.
      for (const record of sortLog.forTank(tankId)) {
        if (record.photoId === photo.id && record.status === 'failed' && !record.dismissed) {
          sortLog.update(record.key, { dismissed: true });
        }
      }
      const record = sortLog.add({
        tankId,
        photoId: photo.id,
        takenAt: photo.taken_at,
        thumbUrl: photo.thumb_url,
        livestockId: null,
        livestockName: null,
        body: null,
        recentsBefore: null,
        ...fields,
      });
      send(record);
      return true;
    },
    [tankId, send]
  );

  const assign = useCallback(
    (input: AssignInput): boolean => {
      const { photo } = input;
      if (sortLog.pendingFor(tankId, photo.id)) return false;
      if (isTankInput(input)) {
        // The tank is pinned first among the chips, never one of the recents.
        return start(photo, {
          kind: 'assign',
          target: 'tank',
          livestockName: TANK_SHOT_NAME,
          body: input.body,
        });
      }
      const { organism, body } = input;
      const recentsBefore = readRecents(tankId);
      // Straight away, so the next photo's Recent chips start with it ("same as last").
      pushRecent(tankId, organism.id);
      return start(photo, {
        kind: 'assign',
        target: 'organism',
        livestockId: organism.id,
        livestockName: organism.common_name,
        body,
        recentsBefore,
      });
    },
    [tankId, start]
  );

  const discard = useCallback(
    (photo: PhotoInboxItem): boolean => start(photo, { kind: 'discard' }),
    [start]
  );

  const retry = useCallback(
    (key: number): void => {
      const record = sortLog.get(key);
      if (!record || record.status !== 'failed' || !record.canRetry) return;
      sortLog.update(key, { status: 'pending', message: null });
      send(record);
    },
    [send]
  );

  /**
   * Opens a sorted photo, replacing a photo already open (back returns to the
   * grid). Its tank is selected first if the user has switched meanwhile.
   */
  const openPhoto = useCallback(
    (record: SortRecord): void => {
      const { location: here } = latest.current;
      const onPhoto = PHOTO_ROUTE.test(here.pathname);
      const fromInbox = (here.state as InboxLocationState | null)?.fromInbox === true;
      const state: InboxLocationState = {
        fromInbox: onPhoto ? fromInbox : INBOX_ROUTE.test(here.pathname),
      };
      void openInTank(record.tankId, `/livestock/photos/${record.photoId}`, {
        replace: onPhoto,
        state,
      });
    },
    [openInTank]
  );

  const undo = useCallback(
    (key: number): void => {
      const record = sortLog.get(key);
      if (!record || !canUndo(record)) return;
      sortLog.update(key, { status: 'undoing', undoMessage: null });
      const target = record.kind === 'assign' ? targetOf(record) : null;
      sendRestore({
        tankId,
        id: record.photoId,
        target,
        livestockId: target === 'organism' ? record.resultLivestockId : null,
      })
        .then(() => {
          const { recentsBefore, livestockId } = record;
          if (recentsBefore && livestockId !== null && readRecents(tankId)[0] === livestockId) {
            writeRecents(tankId, recentsBefore);
          }
          sortLog.update(key, { status: 'undone' });
          openPhoto(record);
        })
        .catch((error: unknown) => {
          if (error instanceof InboxSessionChangedError) return;
          const message = describeUndoFailure(error, target);
          sortLog.update(key, {
            status: 'done',
            undoMessage: message ?? "Couldn't undo. Try again.",
            undoBlocked: message !== null,
          });
        });
    },
    [tankId, sendRestore, openPhoto]
  );

  const open = useCallback(
    (key: number): void => {
      const record = sortLog.get(key);
      if (!record) return;
      sortLog.update(key, { dismissed: true });
      openPhoto(record);
    },
    [openPhoto]
  );

  const dismiss = useCallback((key: number): void => sortLog.update(key, { dismissed: true }), []);

  return useMemo(
    () => ({ assign, discard, retry, undo, open, dismiss }),
    [assign, discard, retry, undo, open, dismiss]
  );
}

export interface SendingStatus {
  /** Assigns and discards of this tank on their way. */
  count: number;
  /** "Assigning 2…", or why it is taking longer; null when nothing is being sent. */
  label: string | null;
}

/** What is being sent for the tank, for the action bar. */
export function useSendingStatus(tankId: string): SendingStatus {
  const records = useSortRecords(tankId);
  const reasons = useMutationState({
    filters: { mutationKey: photoInboxKeys.assignMutation, status: 'pending' },
    select: (mutation) => mutation.state.failureReason,
  });
  const pending = records.filter((record) => record.status === 'pending');
  const count = pending.length;
  if (count === 0) return { count, label: null };
  if (reasons.some((reason) => isConflict(reason, 'in_progress'))) {
    return { count, label: 'Finishing the previous assign…' };
  }
  if (reasons.some((reason) => reason instanceof PhotoInboxBusyError)) {
    return { count, label: 'The server is busy cropping. Trying again…' };
  }
  const discards = pending.filter((record) => record.kind === 'discard').length;
  const verb = discards === count ? 'Discarding' : discards === 0 ? 'Assigning' : 'Sorting';
  return { count, label: `${verb} ${count}…` };
}
